// © 2013 - 2023 Tinder, Inc., ALL RIGHTS RESERVED

import { decamelize, formatLinearGradient } from '@tinderapp/ui-utils';
import { Theme, ThemeValueNamespace, PartialTheme, ExtensionTheme } from '../types';
import {
  FONT_WEIGHT_LIGHT,
  FONT_WEIGHT_REGULAR,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_EXTRABOLD,
  FONT_NAME_PROXIMA_NOVA,
  FONT_FAMILY_BASE,
  FONT_FAMILY_PROXIMA_NOVA,
} from '../constants/typography';

function round(value: number) {
  return Math.round(value * 100) / 100;
}

const THEME_TYPE_TO_CSS_VAR_PREFIX: {
  [key in ThemeValueNamespace | 'dark' | 'gradientFill']: string;
} = {
  color: 'color',
  dark: 'dark',
  fontFamily: 'font-family',
  fontSize: 'font-size',
  fontWeight: 'font-weight',
  gradientFill: 'fill',
  gradient: 'gradient',
  letterCasing: 'letter-casing',
  letterSpacing: 'letter-spacing',
  lineHeight: 'line-height',
  sizing: 'sizing',
};

const FONT_WEIGHT_NUMERIC = {
  [FONT_WEIGHT_LIGHT]: 300,
  [FONT_WEIGHT_REGULAR]: 400,
  [FONT_WEIGHT_MEDIUM]: 500,
  [FONT_WEIGHT_SEMIBOLD]: 600,
  [FONT_WEIGHT_BOLD]: 700,
  [FONT_WEIGHT_EXTRABOLD]: 800,
};

// Maps fontFamily value from the theme to a CSS font-family cascade
const FONT_KEY_TO_FAMILY: { [key: string]: string } = {
  [FONT_NAME_PROXIMA_NOVA]: FONT_FAMILY_PROXIMA_NOVA,
  system: FONT_FAMILY_BASE,
};

export function themeKeyCssVariable(
  type: keyof typeof THEME_TYPE_TO_CSS_VAR_PREFIX,
  key?: string
): string {
  return key
    ? `--${THEME_TYPE_TO_CSS_VAR_PREFIX[type]}--${decamelize(key, '-')}`
    : `--theme-${THEME_TYPE_TO_CSS_VAR_PREFIX[type]}`;
}

export function themeCssVariable(
  type: keyof typeof THEME_TYPE_TO_CSS_VAR_PREFIX,
  key?: string,
  fallback?: string
): string {
  return fallback
    ? `var(${themeKeyCssVariable(type, key)}, ${fallback})`
    : `var(${themeKeyCssVariable(type, key)})`;
}

export function getThemeClassName(themeId: string): string {
  return themeId ? `theme-${themeId}` : '';
}

export function namespaceCssScope(namespace?: string): string {
  return namespace ? `.${getThemeClassName(namespace)}` : ':root';
}

// Colors: Solid color
export function createThemeColorCssVariables(values: Theme['color'] = {}): string {
  return Object.entries(values)
    .map(([key, value]) => `${themeKeyCssVariable('color', key)}: ${value};`)
    .join('\n');
}

// Gradients: Linear gradient or solid color
export function createThemeGradientCssVariables(values: Theme['gradient'] = {}): string {
  return Object.entries(values)
    .map(
      ([key, value]) => `${themeKeyCssVariable('gradient', key)}: ${formatLinearGradient(value)};`
    )
    .join('\n');
}

// Gradients: SVG fill
export function createThemeGradientFillCssVariables(
  values: Theme['gradient'] = {},
  getPatternId: (type: string, patternKey: string) => string
): string {
  // Patterns are assumed to have been injected into the page outside of this,
  // so our job here is just to map the CSS variables to the fill url()
  return Object.keys(values)
    .map((key) => {
      const patternId = getPatternId('linear', key);
      return `${themeKeyCssVariable('gradientFill', key)}: url(#${patternId});`;
    })
    .join('\n');
}

// Typography: Font family (font family cascade string)
export function createThemeFontFamilyCssVariables(
  values: Theme['fontFamily'] = {},
  baseFonts?: typeof FONT_KEY_TO_FAMILY
): string {
  if (baseFonts) {
    return Object.entries(values)
      .map(([key, value]) => {
        // The value may already be the font family cascade instead of just the key
        const fallbackValue = value.endsWith(FONT_FAMILY_BASE) ? value : FONT_FAMILY_BASE;
        return themeKeyCssVariable('fontFamily', key) === themeKeyCssVariable('fontFamily', value)
          ? `${themeKeyCssVariable('fontFamily', key)}: ${
              FONT_KEY_TO_FAMILY[value] || fallbackValue
            };`
          : `${themeKeyCssVariable('fontFamily', key)}: ${
              value in baseFonts ? themeCssVariable('fontFamily', value) : fallbackValue
            };`;
      })
      .filter((v) => v)
      .join('\n');
  }

  return Object.entries(values)
    .map(
      ([key, value]) =>
        `${themeKeyCssVariable('fontFamily', key)}: ${
          FONT_KEY_TO_FAMILY[value] || (value.endsWith(FONT_FAMILY_BASE) ? value : FONT_FAMILY_BASE)
        };`
    )
    .join('\n');
}

// Typography: Font size (pixels; or rems when baseSize is passed in)
export function createThemeFontSizeCssVariables(
  values: Theme['fontSize'] = {},
  baseSize?: number
): string {
  if (baseSize) {
    return Object.entries(values)
      .map(([key, value]) =>
        key === 'base' && value === baseSize
          ? `${themeKeyCssVariable('fontSize', key)}: ${value}px;`
          : `${themeKeyCssVariable('fontSize', key)}: ${round(value / baseSize)}rem;`
      )
      .join('\n');
  }
  return Object.entries(values)
    .map(([key, value]) => `${themeKeyCssVariable('fontSize', key)}: ${value}px;`)
    .join('\n');
}

// Typography: Font weight
export function createThemeFontWeightCssVariables(values: Theme['fontWeight'] = {}): string {
  return Object.entries(values)
    .map(
      ([key, value]) =>
        `${themeKeyCssVariable('fontWeight', key)}: ${FONT_WEIGHT_NUMERIC[value] || value};`
    )
    .join('\n');
}

// Typography: Line height (pixels; or rems when baseSize is passed; or relative when fontSize is passed)
export function createThemeLineHeightCssVariables(
  values: Theme['lineHeight'],
  baseSize?: number
): string;
export function createThemeLineHeightCssVariables(
  values: Theme['lineHeight'],
  fontSize?: Theme['fontSize']
): string;
export function createThemeLineHeightCssVariables(
  values: Theme['lineHeight'],
  baseOrFontSize?: number | Theme['fontSize']
): string {
  if (typeof baseOrFontSize === 'number') {
    return Object.entries(values)
      .map(
        ([key, value]) =>
          `${themeKeyCssVariable('lineHeight', key)}: ${
            value && `${round(value / baseOrFontSize)}rem`
          };`
      )
      .join('\n');
  }
  if (baseOrFontSize) {
    return Object.entries(values)
      .map(
        ([key, value]) =>
          `${themeKeyCssVariable('lineHeight', key)}: ${
            baseOrFontSize[key] ? round(value / baseOrFontSize[key]) : `${value}px`
          };`
      )
      .join('\n');
  }
  return Object.entries(values)
    .map(([key, value]) => `${themeKeyCssVariable('lineHeight', key)}: ${value}px;`)
    .join('\n');
}

// Typography: Letter spacing (pixels; or rems when baseSize is passed)
export function createThemeLetterSpacingCssVariables(
  values: Theme['letterSpacing'],
  baseSize?: number
): string {
  if (baseSize) {
    return Object.entries(values)
      .map(
        ([key, value]) =>
          `${themeKeyCssVariable('letterSpacing', key)}: ${
            value && `${round(value / baseSize)}rem`
          };`
      )
      .join('\n');
  }
  return Object.entries(values)
    .map(
      ([key, value]) => `${themeKeyCssVariable('letterSpacing', key)}: ${value && `${value}px`};`
    )
    .join('\n');
}

// Typography: Letter casing
export function createThemeLetterCasingCssVariables(
  values: Theme['letterCasing'],
  omitDefaults = false
): string {
  return Object.entries(values)
    .filter(omitDefaults ? ([key, value]) => key === 'default' || value !== 'normal' : () => true)
    .map(
      ([key, value]) =>
        `${themeKeyCssVariable('letterCasing', key)}: ${value === 'normal' ? 'none' : value};`
    )
    .join('\n');
}

// Sizing (pixels)
export function createThemeSizingCssVariables(values: Theme['sizing'] = {}): string {
  return Object.entries(values)
    .map(
      ([key, value]) => `${themeKeyCssVariable('sizing', key)}: ${value}${value === 0 ? '' : 'px'};`
    )
    .join('\n');
}

export function createThemeKeyCss(
  theme: Theme,
  cssScope = namespaceCssScope(null),
  options: {
    omitDefaults?: boolean;
    getPatternId?: (type: string, patternKey: string) => string;
  } = {
    omitDefaults: false,
    // @TODO: Pass from react-svg-patterns
    getPatternId: (type, patternKey) => `svg-pattern-${type}__gradient--${patternKey}`,
  }
): string {
  const themeCss = [
    `${themeKeyCssVariable('dark')}: ${+theme.dark};`, // light-on-dark-background flag
    createThemeColorCssVariables(theme.color),
    createThemeGradientCssVariables(theme.gradient),
    options.getPatternId &&
      createThemeGradientFillCssVariables(theme.gradient, options.getPatternId),
    options.omitDefaults &&
      createThemeFontFamilyCssVariables({
        proximaNova: FONT_NAME_PROXIMA_NOVA,
      }),
    createThemeFontFamilyCssVariables(theme.fontFamily, FONT_KEY_TO_FAMILY),
    createThemeFontSizeCssVariables(theme.fontSize, theme.fontSize.base),
    createThemeFontWeightCssVariables(theme.fontWeight),
    createThemeLineHeightCssVariables(theme.lineHeight, theme.fontSize.base),
    createThemeLetterSpacingCssVariables(theme.letterSpacing, theme.fontSize.base),
    createThemeLetterCasingCssVariables(theme.letterCasing, options.omitDefaults),
    createThemeSizingCssVariables(theme.sizing),
  ]
    .filter((v) => v)
    .join('\n');

  return cssScope ? `${cssScope}{${themeCss}}` : themeCss;
}

/**
 * Modifies a theme by merging in a partial theme containing overrides to colors
 * and/or gradients.
 *
 * New keys that are not present in the original theme may be added as well.
 */
export function extendTheme<A extends Theme, B extends ExtensionTheme<A>>(
  themeA: A,
  themeB: B
): A & B {
  const theme = { ...themeA, ...themeB };
  if (themeB.color) {
    theme.color = { ...themeA.color, ...themeB.color };
  }
  if (themeB.gradient) {
    theme.gradient = { ...themeA.gradient, ...themeB.gradient };
  }
  return theme;
}

/**
 * Modifies a theme by merging in a partial theme containing overrides to colors
 * and/or gradients.
 *
 * All keys must be present in the original theme.
 */
export function overrideTheme<T extends Theme>(theme: T, overrides: PartialTheme<T>): T {
  return extendTheme(theme, overrides);
}
